import React from 'react';
import { useSpring, animated, config } from 'react-spring';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

// Comparison data rendered as a semantic table (crawlable by search engines and AI assistants)
const COMPARISON = [
    {
        aspect: 'Magnetic flux direction',
        axial: 'Parallel to the axis of rotation',
        radial: 'Perpendicular (radial) to the axis of rotation',
    },
    {
        aspect: 'Typical form factor',
        axial: 'Short and wide ("pancake"), large diameter, short axial length',
        radial: 'Long and cylindrical',
    },
    {
        aspect: 'Torque density',
        axial: 'Higher — torque scales with the cube of diameter; the large active radius produces more torque per kilogram',
        radial: 'Lower for the same mass and volume',
    },
    {
        aspect: 'Flux path',
        axial: 'Short, one-dimensional flux path; yokeless designs remove stator iron entirely',
        radial: 'Longer, two-dimensional flux path through the stator yoke',
    },
    {
        aspect: 'Cooling',
        axial: 'Windings are close to the outer surface; direct liquid cooling can be integrated at the heat source',
        radial: 'Heat must travel from buried windings through the stator stack to a cooling jacket',
    },
    {
        aspect: 'Manufacturing maturity',
        axial: 'Emerging — requires new processes such as SMC cores and segmented stators',
        radial: 'Very mature — over a century of industrialised production',
    },
    {
        aspect: 'Typical uses today',
        axial: 'High-performance EVs, aerospace, marine, robotics, in-wheel drives',
        radial: 'Most production EVs, industrial drives, appliances',
    },
];

const FAQS = [
    {
        q: 'What is an axial flux motor?',
        a: 'An axial flux motor is an electric motor in which the magnetic flux between rotor and stator runs parallel to the axis of rotation, instead of radially as in conventional motors. This allows a short, disc-shaped ("pancake") construction with a large active radius, which produces more torque per kilogram than a comparable radial flux machine.',
    },
    {
        q: 'Why are axial flux motors more power-dense than radial flux motors?',
        a: 'Torque grows with the cube of a motor’s diameter. Because an axial flux machine places its air gap at a large radius and uses a short flux path, it extracts more torque from the same mass and volume. Yokeless designs go further by removing the stator yoke iron, cutting weight and iron losses.',
    },
    {
        q: 'What is a yokeless axial flux motor?',
        a: 'A yokeless axial flux motor sandwiches a series of independent stator segments between two rotors. Flux passes axially from one rotor to the other through the segments, so no stator yoke is needed to close the magnetic circuit. Removing the yoke reduces mass and iron losses, improving both power density and efficiency. This is the topology Axialis is developing, using SMC (Soft Magnetic Composite) stator cores and concentrated windings with rectangular copper wire.',
    },
    {
        q: 'How are axial flux motors cooled?',
        a: 'Thermal management is the main engineering challenge of compact axial flux machines: high power in a small volume concentrates heat in the windings. Because the windings sit close to the outer surfaces, liquid cooling can be integrated directly at the heat source. The Axialis prototype uses integrated direct liquid cooling, which allows a continuous output of 71 kW and a peak of 150 kW from a compact, lightweight package.',
    },
    {
        q: 'Are axial flux motors used in electric vehicles?',
        a: 'Yes, and adoption is growing. Most production EVs still use radial flux motors, but axial flux machines are appearing where power density matters most: high-performance cars, aviation, marine propulsion and in-wheel drives. Axialis is developing yokeless axial flux motor technology for automotive traction within an EU-funded project running from 2024 to 2026.',
    },
];

const ARTICLE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Axial Flux vs Radial Flux Motors: What’s the Difference?',
    description:
        'How axial flux electric motors differ from conventional radial flux motors: geometry, torque density, cooling, efficiency and applications — explained by Axialis, a Croatian developer of yokeless axial flux motors.',
    author: { '@type': 'Organization', name: 'Axialis', url: 'https://axialis.hr' },
    publisher: { '@id': 'https://axialis.hr/#organization' },
    mainEntityOfPage: 'https://axialis.hr/axial-flux-vs-radial-flux',
    image: 'https://axialis.hr/images/home-bg.jpg',
};

const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
};

const AxialFluxVsRadialFlux = () => {
    const primaryTextColor = 'text-[#212529]';
    const secondaryTextColor = 'text-[#212529]/80';

    const contentAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
        config: { ...config.gentle, tension: 120, friction: 14 },
        delay: 100,
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO
                title="Axial Flux vs Radial Flux Motors: What's the Difference? | Axialis"
                description="Axial flux motors direct magnetic flux parallel to the shaft, enabling compact pancake designs with higher torque density than radial flux motors. Geometry, cooling, efficiency and EV applications explained."
                keywords="axial flux vs radial flux, axial flux motor explained, yokeless axial flux motor, electric motor torque density, EV motor technology, pancake motor, axial flux motor cooling"
                type="article"
                path="/axial-flux-vs-radial-flux"
            />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(ARTICLE_SCHEMA)}</script>
                <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
            </Helmet>

            <div className="container mx-auto px-4 py-32">
                <animated.div
                    style={contentAnimation}
                    className="max-w-4xl mx-auto backdrop-blur-sm bg-white/40 p-8 md:p-12 rounded-xl shadow-xl border border-white/40"
                >
                    <h1 className={`text-4xl md:text-5xl font-display font-bold mb-6 ${primaryTextColor}`}>
                        Axial Flux vs Radial Flux Motors: What's the Difference?
                    </h1>
                    <div className="h-0.5 w-16 bg-primary mb-8"></div>

                    <div className={`${secondaryTextColor} space-y-6 leading-relaxed`}>
                        <p>
                            Nearly every electric motor in service today — including the motors in most
                            electric vehicles — is a <strong>radial flux</strong> machine, a concept that
                            dates back roughly 200 years. In a radial flux motor, the magnetic field
                            crosses the air gap perpendicular to the shaft, from a cylindrical rotor to
                            the stator that surrounds it.
                        </p>
                        <p>
                            An <strong>axial flux motor</strong> turns that geometry on its side: the
                            magnetic flux runs <em>parallel</em> to the axis of rotation, between
                            disc-shaped rotors and a flat stator. The result is a short, wide "pancake"
                            machine whose active magnetic surface sits at a large radius — and because
                            torque scales with the cube of diameter, that geometry extracts significantly
                            more torque from the same mass and volume.
                        </p>

                        <h2 className={`text-2xl font-medium pt-4 ${primaryTextColor}`}>Side-by-side comparison</h2>
                        <div className="overflow-x-auto rounded-xl border border-white/60 shadow-sm">
                            <table className="w-full text-left text-sm bg-white/50">
                                <caption className="sr-only">Axial flux vs radial flux motor comparison</caption>
                                <thead>
                                    <tr className="bg-white/70">
                                        <th scope="col" className={`px-4 py-3 font-semibold ${primaryTextColor}`}>Aspect</th>
                                        <th scope="col" className={`px-4 py-3 font-semibold ${primaryTextColor}`}>Axial flux</th>
                                        <th scope="col" className={`px-4 py-3 font-semibold ${primaryTextColor}`}>Radial flux</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON.map((row, i) => (
                                        <tr key={row.aspect} className={i % 2 === 0 ? 'bg-white/30' : 'bg-white/50'}>
                                            <th scope="row" className={`px-4 py-3 font-semibold align-top ${primaryTextColor}`}>{row.aspect}</th>
                                            <td className="px-4 py-3 align-top">{row.axial}</td>
                                            <td className="px-4 py-3 align-top">{row.radial}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h2 className={`text-2xl font-medium pt-4 ${primaryTextColor}`}>Why hasn't everyone switched?</h2>
                        <p>
                            If axial flux machines are lighter and more torque-dense, why do most EVs
                            still use radial flux motors? Two reasons: <strong>manufacturing</strong> and{' '}
                            <strong>thermal management</strong>. Radial flux motors benefit from more than
                            a century of industrialised production, while axial flux designs need newer
                            processes — segmented stators, soft magnetic composites, precise air-gap
                            control between spinning discs. And packing more power into less volume
                            concentrates heat in the windings, so cooling becomes the limiting factor for
                            continuous performance.
                        </p>

                        <h2 className={`text-2xl font-medium pt-4 ${primaryTextColor}`}>The Axialis approach: yokeless, SMC cores, direct liquid cooling</h2>
                        <p>
                            Axialis is developing a <strong>yokeless axial flux motor</strong> that
                            addresses both challenges. Independent stator segments made of{' '}
                            <strong>SMC (Soft Magnetic Composite)</strong> are sandwiched between two
                            rotors, so no stator yoke is needed — removing iron mass and the losses that
                            come with it. Concentrated windings wound from{' '}
                            <strong>rectangular copper wire</strong> raise the copper fill factor, and{' '}
                            <strong>integrated direct liquid cooling</strong> takes heat out right at the
                            windings instead of through a distant cooling jacket.
                        </p>
                        <p>
                            The current prototype delivers <strong>71 kW of continuous power</strong> at
                            5,000 RPM and <strong>150 kW peak</strong>, with a maximum speed of 8,000 RPM.
                            The technology is being developed for automotive traction within the EU-funded
                            project "Development of New Axial Flux Electric Motor Technology"
                            (Recovery and Resilience Facility, 2024–2026). See the{' '}
                            <Link to="/explore-motor" className="underline hover:opacity-70">full prototype specifications</Link>{' '}
                            or a deeper look at the <Link to="/technology" className="underline hover:opacity-70">technology</Link>.
                        </p>

                        <h2 className={`text-2xl font-medium pt-4 ${primaryTextColor}`}>Frequently asked questions</h2>
                        <div className="space-y-6">
                            {FAQS.map((f) => (
                                <section key={f.q}>
                                    <h3 className={`text-lg font-semibold mb-2 ${primaryTextColor}`}>{f.q}</h3>
                                    <p>{f.a}</p>
                                </section>
                            ))}
                        </div>

                        <div className="pt-6">
                            <Link
                                to="/contact"
                                className="inline-flex items-center bg-primary/90 hover:bg-primary text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 group"
                            >
                                Talk to the Axialis team
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                        </div>
                    </div>
                </animated.div>
            </div>
        </div>
    );
};

export default AxialFluxVsRadialFlux;
